import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Modal,
  Form,
  Collapse
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { getAuthHeaders } from '../utils/authHelpers';
import '../styles/OrderList.css';

// Components
import ManufacturingSchedule from '../components/ManufacturingSchedule';
import ManufacturingBacklog from '../components/ManufacturingBacklog';
import PalletCalculatorModal from '../components/PalletCalculatorModal';
import TaskGroup from '../components/TaskGroup';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

export default function ManufacturingDashboardPage() {
  const {
    user,
    allOrders,
    loading,
    error,
    submissionMessage,
    setSubmissionMessage,
    fetchOrdersForRole,
    orderTaskStates,
    setOrderTaskStates,
    getTasksForStage,
    stageMap,
    formatDate,
    groupTasksForDisplay
  } = useDashboardData();

  /* -------------------- UI & Filtering State -------------------- */
  const [expandedRows, setExpandedRows] = useState({});
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [productionDate, setProductionDate] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [showPalletModal, setShowPalletModal] = useState(false);
  const [showUnscheduleModal, setShowUnscheduleModal] = useState(false);
  const [orderToUnschedule, setOrderToUnschedule] = useState(null);
  const [dateError, setDateError] = useState('');
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [orderToAdvance, setOrderToAdvance] = useState(null);

  /* -------------------- Auto-Dismiss Alerts -------------------- */
  useEffect(() => {
    if (submissionMessage.message) {
      const timer = setTimeout(() => {
        setSubmissionMessage({ message: '', type: '' });
      }, 3000); // 3 seconds

      // Clean up the timer if the component unmounts or if a new message arrives
      return () => clearTimeout(timer);
    }
  }, [submissionMessage, setSubmissionMessage]);


  /* -------------------- Helper Logic -------------------- */
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.getFullYear(), date.getMonth(), diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  /* -------------------- Updated Action Handlers -------------------- */

  const handleScheduleOrder = async () => {
    if (!selectedOrder || !productionDate) return;

    try {
      setScheduling(true);
      await fetch(`${ORDERS_API_URL}/${selectedOrder.orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          manufacturing: { ...selectedOrder.manufacturing, orderEnteredProductionDate: productionDate },
          updatedAt: new Date()
        })
      });

      await fetchOrdersForRole();
      setOrderTaskStates({}); 
      setShowScheduleModal(false);
      setSubmissionMessage({ message: 'Order scheduled successfully.', type: 'success' });
    } catch (err) {
      setSubmissionMessage({ message: 'Scheduling failed.', type: 'danger' });
    } finally { setScheduling(false); }
  };

  /* -------------------- Filter Logic -------------------- */
  
  const dashboardOrders = useMemo(() => {
  const allowedStages = ['Manufacturing', 'Shipping', 'Completed'];
    return allOrders.filter(o => allowedStages.includes(o.currentStage));
  }, [allOrders]);

  /* -------------------- Updated Filter State -------------------- */
  const getMondayString = (date) => {
    const m = getMonday(date);
    return m.toISOString().split('T')[0];
  };

  const currentWeekMonday = getMondayString(new Date());

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(currentWeekMonday);

  const earliestProductionDate = useMemo(() => {
    const dates = allOrders
      .map(o => o.manufacturing?.orderEnteredProductionDate)
      .filter(Boolean)
      .map(d => new Date(d.split('T')[0] + 'T00:00:00'));

    if (dates.length === 0) return new Date();

    return new Date(Math.min(...dates));
  }, [allOrders]);

  const weekOptions = useMemo(() => {
    const weeks = [];

    const start = getMonday(earliestProductionDate);
    const end = getMonday(new Date());
    end.setDate(end.getDate() + 7 * 4)  // Can schedule up to 4 weeks in advance

    let current = new Date(start);

    while (current <= end) {
      weeks.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 7);
    }

    return weeks;
  }, [earliestProductionDate]);

  const yearOptions = useMemo(() => {
    const years = new Set(
      weekOptions.map(w => new Date(w).getFullYear())
    );
    return Array.from(years).sort((a, b) => a - b);
  }, [weekOptions]);

  const monthOptions = useMemo(() => {
    const months = weekOptions
      .filter(w => new Date(w).getFullYear() === selectedYear)
      .map(w => new Date(w).getMonth() + 1);

    return Array.from(new Set(months)).sort((a, b) => a - b);
  }, [weekOptions, selectedYear]);

  const filteredWeekOptions = useMemo(() => {
    return weekOptions.filter(w => {
      const d = new Date(w);
      return (
        d.getFullYear() === selectedYear &&
        d.getMonth() + 1 === selectedMonth
      );
    });
  }, [weekOptions, selectedYear, selectedMonth]);

  /* -------------------- Task Management Logic -------------------- */
  const initialTaskStates = useMemo(() => {
    const map = {};
    allOrders.forEach(order => {
      const stageKey = stageMap[order.currentStage];
      if (!stageKey) return;
      const stageData = order[stageKey] || {};
      const snapshot = {};

      (getTasksForStage(order.currentStage) || []).forEach(task => {
        if (task.type === 'checkbox') snapshot[task.id] = stageData[task.id] || false;
        else if (task.type === 'date') {
          snapshot[task.id] = stageData[task.id] ? new Date(stageData[task.id]).toISOString().split('T')[0] : '';
        }
      });
      map[order.orderId] = snapshot;
    });
    return map;
  }, [allOrders, stageMap, getTasksForStage]);

  useEffect(() => {
    if (Object.keys(orderTaskStates).length === 0 && Object.keys(initialTaskStates).length > 0) {
      setOrderTaskStates(initialTaskStates);
    }
  }, [initialTaskStates, orderTaskStates, setOrderTaskStates]);

  const handleTaskChange = useCallback((orderId, taskId, value) => {
    setOrderTaskStates(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], [taskId]: value }
    }));
  }, [setOrderTaskStates]);

  const isOrderDirty = useCallback((order) => {
    if (order.currentStage !== 'Manufacturing') {
      return false;
    }

    const current = orderTaskStates[order.orderId] || {};
    const initial = initialTaskStates[order.orderId] || {};

    return Object.keys(current).some(key => current[key] !== initial[key]);
  }, [orderTaskStates, initialTaskStates]);

  /* -------------------- Rendering Helpers -------------------- */
  const scheduleOrders = useMemo(() => {
  const allowedStages = ['Manufacturing', 'Shipping', 'Completed'];
  return allOrders.filter(o => allowedStages.includes(o.currentStage));
}, [allOrders]);

  const TaskInputRow = ({ 
    order, 
    task, 
    currentOrderTaskState, 
    handleTaskChange, 
    loading,
    alignDesktop = 'start'
  }) => {

    const flexClass =
      alignDesktop === 'center'
        ? "d-flex justify-content-center"
        : "d-flex justify-content-center justify-content-md-start";

    const textClass =
      alignDesktop === 'center'
        ? "text-center"
        : "text-center text-md-start";

    return (
      <div className="mb-2 w-100">
        {task.type === 'checkbox' ? (
          <div className={flexClass}>
            <Form.Check
              type="checkbox"
              id={`task-${order.orderId}-${task.id}`}
              className="mb-0"
            >
              <Form.Check.Input
                type="checkbox"
                checked={!!currentOrderTaskState[task.id]}
                onChange={(e) =>
                  handleTaskChange(order.orderId, task.id, e.target.checked)
                }
                disabled={
                  loading ||
                  (task.editableBy &&
                    !task.editableBy.includes(user?.role))
                }
              />
              <Form.Check.Label className="ms-2">
                {task.label}
              </Form.Check.Label>
            </Form.Check>
          </div>
        ) : (
          <Form.Group
            controlId={`task-${order.orderId}-${task.id}`}
            className="mb-0 w-100"
          >
            <Form.Label
              className={`mb-1 d-block ${textClass}`}
            >
              {task.label}
              {task.required && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>

            <div className={flexClass}>
              <Form.Control
                type="date"
                value={currentOrderTaskState[task.id] ?? ''}
                onChange={(e) =>
                  handleTaskChange(order.orderId, task.id, e.target.value)
                }
                disabled={
                  loading ||
                  (task.editableBy &&
                    !task.editableBy.includes(user?.role))
                }
                className="w-auto"
              />
            </div>
          </Form.Group>
        )}
      </div>
    );
  };

  const renderExpandedRow = (order, colSpan) => {
    // Completed → No tasks shown
    if (order.currentStage === 'Completed') {
      return null;
    }

    const tasks = getTasksForStage(order.currentStage) || [];
    const state = orderTaskStates[order.orderId] || {};

    return (
      <tr key={`${order.orderId}-expanded`}>
        <td colSpan={colSpan} className="p-0">
          <Collapse in={!!expandedRows[order.orderId]}>
            <div className="p-3 bg-light border-top">

              {tasks.length === 0 ? (
                <small className="text-muted ms-2">
                  No tasks defined for this stage.
                </small>
              ) : order.currentStage === 'Manufacturing' ? (

                /* ============================= */
                /* MANUFACTURING FORMAT (3 COL) */
                /* ============================= */
                (() => {
                  const grouped = groupTasksForDisplay(tasks);
                  const seamstress = grouped.find(
                    g => g.group === 'Seamstress Tasks'
                  );

                  const getS = (name) =>
                    seamstress?.subtasks?.find(
                      s => s.subgroup === name
                    ) || { tasks: [] };

                  return (
                    <Row className="gx-4">
                      <Col md={4}>
                        {grouped
                          .filter(g =>
                            ['Manager Tasks', 'Welder Tasks', 'Assembler Tasks']
                              .includes(g.group)
                          )
                          .map((group, idx) => (
                            <TaskGroup
                              key={idx}
                              group={group}
                              order={order}
                              currentOrderTaskState={state}
                              handleTaskChange={handleTaskChange}
                              loading={loading}
                              TaskInputRow={TaskInputRow}
                            />
                          ))}
                      </Col>

                      <Col md={4}>
                        <TaskGroup
                          group={{
                            group: 'Seamstress Tasks',
                            subtasks: [getS('Drawing'), getS('Cutting')]
                          }}
                          order={order}
                          currentOrderTaskState={state}
                          handleTaskChange={handleTaskChange}
                          loading={loading}
                          TaskInputRow={TaskInputRow}
                        />
                      </Col>

                      <Col md={4}>
                        <TaskGroup
                          group={{
                            group: 'Seamstress Tasks',
                            subtasks: [
                              getS('Sewing'),
                              getS('Completing & Packing')
                            ]
                          }}
                          order={order}
                          currentOrderTaskState={state}
                          handleTaskChange={handleTaskChange}
                          loading={loading}
                          TaskInputRow={TaskInputRow}
                        />
                      </Col>
                    </Row>
                  );
                })()

              ) : (

                /* ======================= */
                /* SHIPPING FORMAT (FLAT) */
                /* ======================= */
                <Row className="gx-4">
                  <Col md={12} className="d-flex flex-column gap-2">
                    {tasks.map(task => (
                      <TaskInputRow
                        key={task.id}
                        order={order}
                        task={task}
                        currentOrderTaskState={state}
                        handleTaskChange={handleTaskChange}
                        loading={loading}
                        alignDesktop="center"
                      />
                    ))}
                  </Col>
                </Row>

              )}
            </div>
          </Collapse>
        </td>
      </tr>
    );
  };

  /* -------------------- Action Handlers -------------------- */
  const handleConfirmSave = async () => {
    setShowConfirmSaveModal(false); 
    const dirtyOrders = dashboardOrders.filter(isOrderDirty);
    
    try {
      // 1. Perform all API updates
      await Promise.all(dirtyOrders.map(async (order) => {
        const stageKey = stageMap[order.currentStage];
        if (!stageKey) return;

        const existingStageData = order[stageKey] || {};
        const newTaskUpdates = orderTaskStates[order.orderId] || {};

        await fetch(`${ORDERS_API_URL}/${order.orderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            [stageKey]: { ...existingStageData, ...newTaskUpdates },
            updatedAt: new Date()
          })
        });
      }));

      // 2. Refresh the data from the server
      await fetchOrdersForRole();

      // 3. IMPORTANT: Reset the local task states so they match the fresh data
      // This removes the "dirty" status and refreshes the UI
      setOrderTaskStates({}); 

      setSubmissionMessage({ message: 'Production tasks updated successfully.', type: 'success' });
    } catch (err) {
      console.error("Save error:", err);
      setSubmissionMessage({ message: 'Failed to update tasks.', type: 'danger' });
    }
  };

  /* -------------------- Unschedule Logic -------------------- */
  const handleOpenUnscheduleModal = (order) => {
    setOrderToUnschedule(order);
    setShowUnscheduleModal(true);
  };

  const handleConfirmUnschedule = async () => {
    if (!orderToUnschedule) return;

    try {
      setScheduling(true);
      await fetch(`${ORDERS_API_URL}/${orderToUnschedule.orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          'manufacturing.orderEnteredProductionDate': null, 
          updatedAt: new Date()
        })
      });
      
      // Refresh database data
      await fetchOrdersForRole();
      
      // Reset local state to clear "dirty" flags so the Save button resets
      setOrderTaskStates({}); 
      
      setShowUnscheduleModal(false);
      setSubmissionMessage({ message: 'Order moved back to backlog.', type: 'info' });
    } catch (err) {
      setSubmissionMessage({ message: 'Failed to unschedule.', type: 'danger' });
    } finally {
      setScheduling(false);
      setOrderToUnschedule(null);
    }
  };

  // Add this helper to bridge the component prop to your existing logic
  const handleGlobalSave = () => {
    if (dashboardOrders.some(isOrderDirty)) {
      setShowConfirmSaveModal(true);
    }
  };

  const handleJumpToCurrentWeek = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
    setSelectedWeek(currentWeekMonday);
  };

  const handleAdvanceStageClick = (order) => {
    if (isOrderDirty(order)) {
      setSubmissionMessage({
        message: 'Please save task changes before marking this order as done.',
        type: 'danger'
      });
      return;
    }

    setOrderToAdvance(order);
    setShowAdvanceModal(true);
  };

  const handleConfirmAdvance = useCallback(async () => {
    if (!orderToAdvance) return;

    setSubmissionMessage({ message: '', type: '' });
    setShowAdvanceModal(false);

    const { orderId, dealerName, customerName } = orderToAdvance;

    const order = allOrders.find(o => o.orderId === orderId);
    if (!order) {
      setSubmissionMessage({ message: 'Order not found in current view.', type: 'danger' });
      return;
    }

    let nextStage = '';
    let updatePayload = {};

    const isPickup = (order.shippingMethod || '').toLowerCase() === 'pickup';

    // Pickup orders skip Shipping and go straight to Completed
    if (isPickup) {
        nextStage = 'Completed';
        updatePayload = {
        currentStage: nextStage,
        completionDate: new Date(),
        manufacturing: { ...(order.manufacturing || {}), status: 'Completed', completionDate: new Date()}
        };
    }
    // Shipped orders follow normal flow
    else {
        nextStage = 'Shipping';
        updatePayload = {
        currentStage: nextStage,
        completionDate: new Date(),
        manufacturing: { ...(order.manufacturing || {}), status: 'Completed', completionDate: new Date()},
        shipping: { managerName: user?.username || 'System', status: 'Pending', startDate: new Date()}
        };
    }

    try {
      const res = await fetch(`${ORDERS_API_URL}/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updatePayload)
      });
      const result = await res.json();

      if (res.ok) {
        await fetchOrdersForRole();
        setSubmissionMessage({ message: `Order for ${dealerName} - ${customerName} successfully moved to ${nextStage}!`, type: 'success' });
      } else {
        setSubmissionMessage({
          message: result.message || `Failed to move order ${orderId} to ${nextStage}.`,
          type: 'danger'
        });
      }
    } catch (e) {
      setSubmissionMessage({ message: 'Network error or server unreachable while advancing order.', type: 'danger' });
    } finally {
      setOrderToAdvance(null);
    }
  }, [orderToAdvance, allOrders, user, setSubmissionMessage, fetchOrdersForRole]);
  
  useEffect(() => {
    if (monthOptions.length === 0) return;

    // Only reset if current month is no longer valid
    if (!monthOptions.includes(selectedMonth)) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions, selectedMonth]);


  useEffect(() => {
    if (filteredWeekOptions.length === 0) return;

    // Only reset if current week is no longer valid
    if (!filteredWeekOptions.includes(selectedWeek)) {
      setSelectedWeek(filteredWeekOptions[0]);
    }
  }, [filteredWeekOptions, selectedWeek]);

  useEffect(() => {
    if (!showScheduleModal) {
      setDateError('');  // clear error when modal closes
    }
  }, [showScheduleModal]);


  // Early returns
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Manufacturing') return <Navigate to="/dashboard" replace />;

  return (
    <Container fluid className="manufacturing-dashboard py-4" style={{ backgroundColor: '#f4f7f9'}}>
      <Container>
        <Card className="mb-4 border-0 shadow-sm text-center py-3">
          <h1>Manufacturing Dashboard</h1>
          <p className="text-muted mb-0">Manage Schedules, Backlog, and Task Progression</p>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {submissionMessage.message && <Alert variant={submissionMessage.type}>{submissionMessage.message}</Alert>}

        {/* Filters & Global Actions */}
        <div className="d-flex flex-column gap-2 mb-2 bg-white p-3 rounded shadow-sm">
          
          {/* The Title - Removed mt-5 to prevent excessive top spacing */}
          <h2 className="mb-3">
            <i className="bi bi-calendar3 me-2"></i>
            Weekly Production Schedule
          </h2>
          
          {/* The Controls - This stays a row */}
          <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
            <Form.Select
              style={{ minWidth: '100px', width: 'auto' }}
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>

            <Form.Select
              style={{ minWidth: '140px', width: 'auto' }}
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
            >
              {monthOptions.map(m => (
                <option key={m} value={m}>
                  {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              style={{ minWidth: '220px', width: 'auto' }}
              value={selectedWeek}
              onChange={e => setSelectedWeek(e.target.value)}
            >
              {filteredWeekOptions.map(w => {
                const dateObj = new Date(w + 'T00:00:00');
                const label = dateObj.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                });
                return (
                  <option key={w} value={w}>
                    Week of {label}
                  </option>
                );
              })}
            </Form.Select>

            <Button 
              variant="outline-secondary" 
              onClick={handleJumpToCurrentWeek}
              style={{ whiteSpace: 'nowrap' }}
            >
              Current Week
            </Button>
          </div>
        </div>


        {/* Schedule Component */}
        <section className="mb-5 printable-schedule">
          <ManufacturingSchedule 
            orders={scheduleOrders}
            selectedWeek={selectedWeek}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onUnschedule={handleOpenUnscheduleModal}
            onChangeDate={(o) => { 
              setSelectedOrder(o); 
              setProductionDate(o.manufacturing?.orderEnteredProductionDate?.split('T')[0] || ''); 
              setShowScheduleModal(true); 
            }}
            onSaveChanges={handleGlobalSave}
            onMarkAsDone={handleAdvanceStageClick}
            toggleRow={id => setExpandedRows(p => ({ ...p, [id]: !p[id] }))}
            expandedRows={expandedRows}
            renderExpandedRow={renderExpandedRow}
            isOrderDirty={isOrderDirty}
            formatDate={formatDate}
          />
        </section>

        {/* Backlog Component */}
        <section className='hide-on-print'>
          <ManufacturingBacklog 
            /* Ensure only Manufacturing stage orders without a date show here */
            orders={dashboardOrders.filter(o => 
              !o.manufacturing?.orderEnteredProductionDate && 
              o.currentStage === 'Manufacturing'
            )}
            loading={loading}
            onSchedule={(o) => { setSelectedOrder(o); setProductionDate(''); setShowScheduleModal(true); }}
            toggleRow={id => setExpandedRows(p => ({ ...p, [id]: !p[id] }))}
            expandedRows={expandedRows}
            renderExpandedRow={renderExpandedRow}
            isOrderDirty={isOrderDirty}
            formatDate={formatDate}
          />
        </section>

        <div className="bg-white p-3 rounded shadow-sm border mb-2 mt-3 hide-on-print">
            <h2 className="mb-3"><i className="bi bi-list-task me-2"></i>Other Tools</h2>
            <Button variant="info" onClick={() => setShowPalletModal(true)}>Pallet Calculator</Button>
          </div>

        {/* Modals (Schedule, Confirm Save, Pallet) */}
        <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} centered>
          <Modal.Header closeButton><strong>Set Production Date</strong></Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Production Date for {selectedOrder?.customerName}</Form.Label>
              <Form.Control
                type="date"
                value={productionDate}
                onChange={(e) => {
                  const selected = new Date(e.target.value + 'T00:00:00');
                  const day = selected.getDay(); // 0 = Sunday

                  // Calculate the start of the current week (Monday)
                  const today = new Date();
                  const currentMonday = getMonday(today);

                  // Set 4-week bounds relative to current week
                  const minDate = new Date(currentMonday);
                  minDate.setDate(minDate.getDate() - 28); // 4 weeks ago
                  const maxDate = new Date(currentMonday);
                  maxDate.setDate(maxDate.getDate() + 28); // 4 weeks from current week

                  // Set the selected value
                  setProductionDate(e.target.value);

                  // Validation
                  if (day === 0) {
                    setDateError('Sundays cannot be selected.');
                  } else if (selected < minDate) {
                    setDateError(`Date cannot be earlier than ${minDate.toLocaleDateString()}.`);
                  } else if (selected > maxDate) {
                    setDateError(`Date cannot be later than ${maxDate.toLocaleDateString()}.`);
                  } else {
                    setDateError(''); // valid
                  }
                }}
                min={(() => {
                  const today = new Date();
                  const currentMonday = getMonday(today);
                  const d = new Date(currentMonday);
                  d.setDate(d.getDate() - 28);
                  return d.toISOString().split('T')[0];
                })()}
                max={(() => {
                  const today = new Date();
                  const currentMonday = getMonday(today);
                  const d = new Date(currentMonday);
                  d.setDate(d.getDate() + 33);
                  return d.toISOString().split('T')[0];
                })()}
              />
              {/* Error Message Below Form */}
              {dateError && (
                <div className="text-danger mt-2">
                  {dateError}
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={handleScheduleOrder}
              disabled={!productionDate || !!dateError || scheduling} // disable if invalid
            >
              Confirm Date
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showConfirmSaveModal} onHide={() => setShowConfirmSaveModal(false)} centered>
          <Modal.Header closeButton className="bg-success text-white"><Modal.Title>Confirm Tasks Updates</Modal.Title></Modal.Header>
          <Modal.Body className="text-center py-4">
            Are you sure you want to save task updates for {dashboardOrders.filter(isOrderDirty).length} order(s)?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmSaveModal(false)}>Cancel</Button>
            <Button variant="success" onClick={handleConfirmSave}>Save Changes</Button>
          </Modal.Footer>
        </Modal>

        {/* Unschedule Confirmation Modal */}
        <Modal show={showUnscheduleModal} onHide={() => setShowUnscheduleModal(false)} centered>
          <Modal.Header closeButton className="bg-warning">
            <Modal.Title>Move to Backlog</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            Are you sure you want to remove <strong>{orderToUnschedule?.customerName}</strong> from the production schedule?
            <p className="text-muted small mt-2">This will move the order back to the manufacturing backlog.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUnscheduleModal(false)}>Cancel</Button>
            <Button variant="warning" onClick={handleConfirmUnschedule} disabled={scheduling}>
              {scheduling ? 'Moving...' : 'Confirm Move'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Advance Stage Modal */}
        <Modal show={showAdvanceModal} onHide={() => setShowAdvanceModal(false)} centered>
          <Modal.Header closeButton className='bg-success text-white'><Modal.Title>Confirm Stage Advance</Modal.Title></Modal.Header>
          <Modal.Body className='text-center py-4'>
            Are you sure you want to mark order <strong>{orderToAdvance?.dealerName} - {orderToAdvance?.customerName}</strong> as done and send it to the next stage <strong>
            {orderToAdvance?.shippingMethod?.toLowerCase() === 'pickup'
            ? 'Completed'
            : 'Shipping'}
            </strong>?
            <Alert variant="info" className="mt-3 mb-0">The dealer will be notified of this stage update.</Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdvanceModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleConfirmAdvance}>
              Confirm & Send
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Pallet Calculator Modal */}
        <PalletCalculatorModal show={showPalletModal} handleClose={() => setShowPalletModal(false)} />
      </Container>
    </Container>
  );
}